import { useState } from 'react';
import { api, errMsg } from '../lib/api';
import { getToken } from '../lib/auth';
import { ProductIcon } from '../lib/svg';
import PhrasePicker from './PhrasePicker';

export interface ProductLike {
  id: string;
  name: string;
  blessing: string | null;
  tier: 'free' | 'paid';
  priceCents: number;
  originalPriceCents: number | null;
  iconSvg: string | null;
}

/** 所有模板共用的敬献弹窗 */
export default function DedicateModal({
  memorialId,
  product,
  onClose,
  onSuccess,
  onNeedLogin,
}: {
  memorialId: string;
  product: ProductLike;
  onClose: () => void;
  onSuccess: () => void;
  onNeedLogin: () => void;
}) {
  const [dedication, setDedication] = useState(product.blessing ?? '');
  const [fromName, setFromName] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 真正需要付费的判定：层级为 paid 且价格 > 0（避免「付费但价 0」的死循环）
  const isPaid = product.tier === 'paid' && product.priceCents > 0;

  async function submit() {
    setLoading(true);
    setError('');

    if (isPaid && !getToken()) {
      onNeedLogin();
      return;
    }

    const body = {
      memorialId,
      productId: product.id,
      dedication,
      fromName: anonymous ? null : fromName,
      isAnonymous: anonymous,
    };

    if (isPaid) {
      const res = await api<{ orderId: string; payUrl: string }>('/orders', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setLoading(false);
      if (res.ok && res.data) {
        window.location.href = res.data.payUrl;
      } else {
        setError(errMsg(res, '下单失败'));
      }
      return;
    }

    const res = await api('/offerings/free', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    setLoading(false);
    if (res.ok) {
      onSuccess();
      onClose();
    } else {
      setError(errMsg(res, '敬献失败'));
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-t-3xl bg-[var(--c-surface)] p-6 sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[var(--c-accent-soft)]">
            <ProductIcon iconSvg={product.iconSvg} size={48} />
          </div>
          <h3 className="mt-3 font-serifcn text-lg text-[var(--c-text)]">{product.name}</h3>
          {isPaid ? (
            <p className="mt-1 text-sm text-[var(--c-accent)]">
              ${(product.priceCents / 100).toFixed(2)}
              {product.originalPriceCents && (
                <span className="ml-2 text-xs text-[var(--c-muted)] line-through">
                  ${(product.originalPriceCents / 100).toFixed(2)}
                </span>
              )}
            </p>
          ) : (
            <p className="mt-1 text-xs text-[var(--c-muted)]">免费敬献</p>
          )}
        </div>

        <div className="mb-4">
          <label className="mb-1.5 block text-xs text-[var(--c-muted)]">寄语</label>
          <textarea
            value={dedication}
            onChange={(e) => setDedication(e.target.value)}
            rows={2}
            placeholder="写下你想说的话…"
            className="w-full rounded-xl border border-[var(--c-border)] bg-[var(--c-bg)] p-3 text-sm text-[var(--c-text)] outline-none"
          />
          <PhrasePicker onPick={(t) => setDedication(t)} label="选用寄语常用语" />
        </div>

        <div className="mb-4">
          <label className="mb-1.5 block text-xs text-[var(--c-muted)]">署名</label>
          <input
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            disabled={anonymous}
            placeholder="女儿 Sarah"
            className="h-11 w-full rounded-xl border border-[var(--c-border)] bg-[var(--c-bg)] px-3 text-sm text-[var(--c-text)] outline-none disabled:opacity-40"
          />
        </div>

        <label className="mb-5 flex items-center gap-2 text-xs text-[var(--c-muted)]">
          <input
            type="checkbox"
            checked={anonymous}
            onChange={(e) => setAnonymous(e.target.checked)}
          />
          匿名敬献
        </label>

        {error && (
          <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
        )}

        <button
          onClick={submit}
          disabled={loading}
          className="h-12 w-full rounded-xl bg-[var(--c-text)] text-sm text-[var(--c-bg)] transition disabled:opacity-50"
        >
          {loading ? '处理中…' : isPaid ? '去支付' : '敬献'}
        </button>

        <button onClick={onClose} className="mt-2 h-11 w-full text-sm text-[var(--c-muted)]">
          取消
        </button>
      </div>
    </div>
  );
}
